import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Box, Grid, Typography } from "@mui/material";
import "../../components-styles.scss";
import "../../Dossiers/Dossier/dossier-styles.scss";
import {
    OverViewWidgetItem,
    Session,
    DossierStatusEnum,
    ClientDashBoardNavBarMenuItemId,
    DossierDetailType,
} from "../../../types";
import { LayoutBarContext } from "../../../contexts";

import ReactSvgIcon from "../../UIElements/ReactSvgIcon";
import { ReactComponent as DossierAttente } from "../../../public/icons/DossierAttente.svg";
import { ReactComponent as DossierEdit } from "../../../public/icons/DossierEdit.svg";
import { ReactComponent as DossierCheck } from "../../../public/icons/DossierCheck.svg";
import { ReactComponent as DossierIcon } from "../../../public/icons/activeIcons/ActiveDossierIcon.svg";
import OverViewWidgets from "../../OverViewWigets/OverViewWidgets";
import HomeHeader from "../../Common/HomeHeader/HomeHeader";
import { useDossier } from "../../Dossiers/useDossier";
import OverViewDossierWrapper from "../../Dossiers/Dossier/OverViews/OverViewDossierWrapper";
import { dossierStorageKey, sessionStorageKey } from "../../../consts";
import { useLocalStorage } from "../../../hooks";
import { getDossiers } from "../../../services/dossier.service";
import { getDossierStatus } from "../../../services/dossier.service";
import Dossiers from "../../Dossiers/Dossiers";
import {
    getDossierStatusByCode,
    getDossierStatusesIdsByUserRole,
} from "../../Dossiers/utils";
import CustomSwapVert from "../../UIElements/CustomSwapVert";
import { sortAscending, sortDescending } from "../../utils";

export default function Home() {
    const { updateContent, setActive } = useContext(LayoutBarContext);

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        auth: { user: currentUser, currentUserRoleId: userRoleId },
        [dossierStorageKey]: { dossierStatus },
    } = session;

    const counterStruct: any = {
        all: 0,
        attente: 0,
        instruction: 0,
        instruit: 0,
        somme: function () {
            this.all = this.attente + this.instruction + this.instruit;
        },
    };

    const [dossiersCount, setDossiersCount] = useState(counterStruct);
    const [dossierStatuses, setDossierStatuses] = useState([]);
    const [isDescendingSort, setisDescendingSort] = useState<boolean>(true);
    const setUpDossierStatuses = useCallback(async () => {
        const statuses = await getDossierStatus();
        if (!statuses.detail) {
            setDossierStatuses(statuses);
        }
    }, []);
    useEffect(() => {
        setUpDossierStatuses();
    }, [setUpDossierStatuses]);

    const dossierCounter = useCallback(async () => {
        if (dossierStatuses.length > 0) {
            const allDossiers = currentUser?.id
                ? await getDossiers(currentUser?.id, {
                      userRoleId,
                      dossiersStatuses: getDossierStatusesIdsByUserRole(
                          userRoleId,
                          dossierStatus
                      ),
                  })
                : [];

            Object.values(DossierStatusEnum).forEach((code) => {
                const idStatus = (
                    getDossierStatusByCode(code, dossierStatuses) || {}
                ).id;

                switch (code) {
                    case DossierStatusEnum.AFFECTE:
                        dossiersCount.attente = allDossiers.filter(
                            (dossier: any) => {
                                return dossier.dossierStatus === idStatus;
                            }
                        ).length;
                        break;
                    case DossierStatusEnum.ATTENTE_DECISION:
                        dossiersCount.instruit = allDossiers.filter(
                            (dossier: any) => {
                                return dossier.dossierStatus === idStatus;
                            }
                        ).length;
                        break;
                    case DossierStatusEnum.EN_COURS_D_INSTRUCTION:
                        dossiersCount.instruction = allDossiers.filter(
                            (dossier: any) => {
                                return dossier.dossierStatus === idStatus;
                            }
                        ).length;
                        break;
                    default:
                        break;
                }
            });
            dossiersCount.somme();
            setDossiersCount(dossiersCount);
        }
    }, [dossierStatuses, dossiersCount]);

    const {
        currentDossiers,
        _fetchCurrentDossiers,
        fetchRegionList,
        regionList,
    } = useDossier();

    useEffect(() => {
        dossierCounter();
    }, [dossierCounter]);

    useEffect(() => {
        fetchRegionList();
    }, [fetchRegionList]);
    const overViewWidgetItems: OverViewWidgetItem[] = useMemo(
        () => [
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierIcon}
                    />
                ),
                title: `${dossiersCount.all} dossier(s) au total`,
                onClick: () => {
                    updateContent(<Dossiers />);
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierAttente}
                    />
                ),
                title: `${dossiersCount.attente} dossier(s) en attente d'instruction`,
                onClick: () => {
                    updateContent(
                        <Dossiers
                            dossierStatusFilter={{
                                code: DossierStatusEnum.AFFECTE,
                            }}
                        />
                    );
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierCheck}
                    />
                ),
                title: `${dossiersCount.instruction} dossier(s) en cours d'instruction`,
                onClick: () => {
                    updateContent(
                        <Dossiers
                            dossierStatusFilter={{
                                code: DossierStatusEnum.EN_COURS_D_INSTRUCTION,
                            }}
                        />
                    );
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
            {
                icon: (
                    <ReactSvgIcon
                        width="1.5rem"
                        height="1.5rem"
                        ReactIcon={DossierEdit}
                    />
                ),
                title: `${dossiersCount.instruit} dossier(s) instruit(s)`,
                onClick: () => {
                    updateContent(
                        <Dossiers
                            dossierStatusFilter={{
                                code: DossierStatusEnum.ATTENTE_DECISION,
                            }}
                        />
                    );
                    setActive(ClientDashBoardNavBarMenuItemId.DOSSIERS);
                },
            },
        ],
        [dossiersCount.all]
    );

    const fetchCurrentDossiers = useCallback(async () => {
        await _fetchCurrentDossiers();
    }, []);

    const currentDossiersSorted = useMemo(() => {
        if (!currentDossiers.length) {
            return currentDossiers;
        }
        if (isDescendingSort) {
            return sortDescending(
                currentDossiers,
                (d: DossierDetailType) => d.assignedAt
            );
        } else {
            return sortAscending(
                currentDossiers,
                (d: DossierDetailType) => d.assignedAt
            );
        }
    }, [currentDossiers, isDescendingSort]);

    useEffect(() => {
        fetchCurrentDossiers();
    }, [fetchCurrentDossiers]);

    return (
        <>
            <header className="dossier-header">
                <Box
                    sx={{
                        marginLeft: "6rem",
                        marginTop: "5vh",
                        marginRight: "1.5rem",
                        marginBottom: "2rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2.28rem",
                        "@media (max-width:1024px)": {
                            marginLeft: "1.5rem",
                        },
                    }}
                >
                    <HomeHeader
                        profilName={
                            currentUser.firstName + " " + currentUser.lastName
                        }
                        title={"Bienvenue sur votre espace instructeur SOCOTEC"}
                    />
                </Box>
            </header>
            <main className="layout-body" style={{ overflow: "hidden" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "3rem",
                        marginLeft: "6rem",
                        marginRight: "6rem",
                        marginTop: "2rem",
                        width: "100%",
                        "@media (max-width:1024px)": {
                            marginRight: "1.5rem",
                            marginLeft: "1.5rem",
                            width: "100vh",
                            marginBottom: "5rem",
                        },
                        "@media (min-width: 600px) and (max-width:1024px)": {
                            width: "100vw",
                        },
                    }}
                >
                    <Grid container spacing={1.5}>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-end",
                                    alignSelf: "stretch",
                                    gap: "0.5rem",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "Roboto",
                                        fontSize: "1.5rem",
                                        fontStyle: "normal",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        color: "#023",
                                    }}
                                >
                                    Dossiers
                                </Typography>
                                <CustomSwapVert
                                    onClick={() => {
                                        setisDescendingSort(!isDescendingSort);
                                    }}
                                    rotate={isDescendingSort}
                                />
                            </Box>

                            {currentDossiers.length
                                ? currentDossiersSorted.map((cd: any) => {
                                      return (
                                          <OverViewDossierWrapper
                                              key={cd.id}
                                              dossierDetail={cd}
                                              regionList={regionList}
                                          />
                                      );
                                  })
                                : null}
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "1.5rem",
                                width: "100%",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "Roboto",
                                    fontSize: "1.5rem",
                                    fontStyle: "normal",
                                    fontWeight: 500,
                                    lineHeight: "normal",
                                    color: "#023",
                                }}
                            >
                                Vue d’ensemble
                            </Typography>
                            <OverViewWidgets
                                overViewWidgetItems={overViewWidgetItems}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </main>
        </>
    );
}
